import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import safeproLogo from '../assets/safepro-logo.svg';
const { version } = require('../../package.json');

// Backendin SSO-callbackin virhekoodit (routes/auth.js, ssoFail) → käännösavain
const SSO_ERRORS = ['not_configured', 'idp_error', 'expired', 'invalid_state', 'tenant_not_enabled', 'user_not_found', 'failed'];

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

// Kirjautumisen jälkeinen paluuosoite (esim. sähköpostin linkki hälytykseen, #32).
// App.js:n RequireAuth tallentaa sen sessionStorageen, joka säilyy myös SSO:n
// Microsoft-kierroksen yli samassa välilehdessä. Vain sovelluksen sisäiset polut.
// Arvo vain luetaan tässä (render), RequireAuth tyhjentää sen perillä.
function takeReturnPath() {
  let path = null;
  try { path = sessionStorage.getItem('safedigi_return_to'); } catch { /* ei sessionStoragea */ }
  return path && path.startsWith('/') && !path.startsWith('//') ? path : '/dashboard';
}

export default function LoginPage() {
  const { user, signIn, signInWithToken } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  // SSO-paluu: token URL-fragmentissa tai virhekoodi query-parametrissa.
  // Osoiteriviltä poistetaan molemmat heti, jotta token ei jää historiaan.
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const query = new URLSearchParams(window.location.search);
    const ssoToken = hash.get('sso_token');
    const ssoError = query.get('sso_error');
    if (ssoToken || ssoError) window.history.replaceState(null, '', window.location.pathname);

    if (ssoError) {
      setError(t(`login.ssoErrors.${SSO_ERRORS.includes(ssoError) ? ssoError : 'failed'}`));
    } else if (ssoToken) {
      setLoading(true);
      signInWithToken(ssoToken)
        .catch(() => setError(t('login.ssoErrors.failed')))
        .finally(() => setLoading(false));
    }

    api.get('/api/auth/sso/config')
      .then(({ data }) => setSsoEnabled(Boolean(data?.enabled)))
      .catch(() => setSsoEnabled(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user) return <Navigate to={takeReturnPath()} replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
    } catch (err) {
      if (err.response?.status === 401) setError(t('login.invalidCredentials'));
      else if (err.response?.data?.code === 'sso_required') setError(t('login.ssoRequired'));
      else setError(t('login.error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <img src={safeproLogo} alt="Safe Digi" className="h-12 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900">Safe Digi</h1>
          <p className="text-sm text-gray-500 mt-1">{t('login.title')}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {ssoEnabled && (
            <>
              <a
                href={`${api.defaults.baseURL}/api/auth/sso/start`}
                className="w-full flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-800 font-medium py-2.5 rounded-lg text-sm transition-colors"
              >
                <MicrosoftLogo />{t('login.ssoButton')}
              </a>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs text-gray-400">{t('login.or')}</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
            </>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('login.email')}</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full rounded-lg border-gray-300 text-sm focus:ring-brand-600 focus:border-brand-600"
                placeholder={t('common.emailPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('login.password')}</label>
              <input
                type="password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full rounded-lg border-gray-300 text-sm focus:ring-brand-600 focus:border-brand-600"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-700 hover:bg-brand-900 text-white font-medium py-2.5 rounded-lg text-sm transition-colors disabled:opacity-60"
            >
              {loading ? t('login.loading') : t('login.submit')}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/unohtuiko-salasana" className="text-xs text-brand-700 hover:underline">
              {t('login.forgot')}
            </Link>
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-300">v{version}</p>
        </div>
      </div>
    </div>
  );
}
